import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { ConfirmDialog, EmailPreview, EmptyState, PushPreview, SidePanel, SmsPreview, StatusChip } from "../../Components/Kit";
import { fetchTemplateGrid, openEditor, previewEditor, resetEditor, saveEditor, saveEventSwitch, saveGroupSwitch, testEditor } from "../../Helpers/notificationConsoleApi";
import TemplateEditor from "../../Components/TemplateEditor";
import WaChatPreview from "../../Components/TemplateEditor/WaChatPreview";
import { AUDIENCE_LABEL, CATEGORY_LABEL, eventName } from "./shared";

// Notification Centre > Messages (R6.3): every event in one table, one row per audience, one cell per channel.
// A cell opens the side panel (on / off, the wording used, preview, test); "Edit wording" opens the full
// editor. Replaces the Templates tab.
const CHANNELS = [
    { key: "push", label: "Push", long: "push and inbox" },
    { key: "whatsapp", label: "WhatsApp", long: "WhatsApp" },
    { key: "email", label: "Email", long: "email" },
    { key: "sms", label: "SMS", long: "SMS" },
];
const BAD_AT_META = ["REJECTED", "PAUSED", "DISABLED"];
// The look of a cell: kind (for the status filter), tone, label.
const cellOf = (row, cell) => {
    if (!cell?.applicable) return { kind: "na", label: `Not for ${AUDIENCE_LABEL[row.audience].toLowerCase()}s` };
    if (!row.enabled) return { kind: "off", label: "Off", sub: "Message switched off" };
    if (!cell.inUse) return { kind: "off", label: "Off", sub: "Not sent by this channel" };
    if (cell.state === "off") return { kind: "off", label: "Off", sub: "Wording switched off" };
    if (cell.state === "library_missing") return { kind: "bad", label: "Built-in (fallback)", sub: "Template archived or deleted" };
    if (cell.state === "pending_approval") {
        const s = String(cell.waStatus || "").toUpperCase();
        if (BAD_AT_META.includes(s)) return { kind: "bad", label: `${s.charAt(0)}${s.slice(1).toLowerCase()} by Meta`, sub: cell.libraryName || "" };
        return { kind: "wait", label: s === "DRAFT" ? "Not sent to Meta" : s === "NOT_SYNCED" ? "Not checked with Meta" : "Waiting for Meta", sub: cell.libraryName || "" };
    }
    if (cell.state === "library") return { kind: "lib", label: "Library", sub: cell.libraryName || "" };
    if (cell.state === "custom") return { kind: "own", label: "Own wording", sub: cell.languages?.length > 1 ? cell.languages.join(", ") : "" };
    if (cell.state === "none") return { kind: "built", label: "No wording", sub: "Nothing is sent" };
    return { kind: "built", label: "Built-in" };
};
const STATUS_FILTERS = [
    { key: "all", label: "All", match: () => true },
    { key: "attention", label: "Needs attention", match: (k) => k === "bad" || k === "wait" },
    { key: "own", label: "Own wording", match: (k) => k === "own" || k === "lib" },
    { key: "built", label: "Built-in", match: (k) => k === "built" },
    { key: "off", label: "Off", match: (k) => k === "off" },
];
const KIND_NOTE = {
    own: { tone: "own", chip: "Written for this message", note: "Private to this message. Share it to the Library from the editor to use it elsewhere." },
    lib: { tone: "info", chip: "Library template", note: "Shared: edits in the Library reach every message that uses it." },
    built: { tone: "neutral", chip: "Built-in", note: "The wording that ships with the platform. Edit it to make your own." },
    off: { tone: "neutral", chip: "Switched off", note: "Nothing is sent on this channel for this message." },
    wait: { tone: "wait", chip: "Waiting for Meta", note: "Until Meta approves it, this channel is skipped." },
    bad: { tone: "bad", chip: "Needs attention", note: "The built-in wording, or nothing, is sent meanwhile." },
};

// The channel's preview in the side panel.
const Preview = ({ channel, shown }) => {
    if (!shown) return <Skeleton height={120} />;
    if (channel === "push") return <PushPreview title={shown.push?.title} body={shown.push?.body} image={shown.push?.image || ""} />;
    if (channel === "email") return <EmailPreview subject={shown.email?.subject} html={shown.email?.html} width={360} />;
    if (channel === "sms") return <SmsPreview text={shown.sms?.text} />;
    if (shown.whatsapp?.linked) return <div className="nc-block small">Meta template <strong>{shown.whatsapp.name || "(none chosen)"}</strong>, with {(shown.whatsapp.values || []).filter(Boolean).length} value(s) filled.</div>;
    return shown.whatsapp ? <WaChatPreview shown={shown.whatsapp} /> : <div className="nc-sub">Nothing to show.</div>;
};

const Panel = ({ sel, row, event, defaultLanguage, onPick, onClose, onChanged, onEdit }) => {
    const [info, setInfo] = useState(null);
    const [shown, setShown] = useState(null);
    const [busy, setBusy] = useState("");
    const [to, setTo] = useState("");
    const [confirmReset, setConfirmReset] = useState(false);
    const target = useMemo(() => ({ kind: "event", eventKey: sel.eventKey, audience: sel.audience, channel: sel.channel, language: defaultLanguage }), [sel, defaultLanguage]);
    const cell = row.cells[sel.channel];
    const look = cellOf(row, cell);

    const load = useCallback(async () => {
        setInfo(null); setShown(null);
        const opened = await openEditor(target);
        if (!opened.ok) { toast.error(opened.message); return; }
        setInfo(opened.data);
        const source = opened.data.source === "library" ? "library" : "own";
        const result = await previewEditor({ target, enabled: true, source, libraryId: opened.data.picked?.libraryId, content: opened.data.content });
        setShown(result.ok ? result.data : {});
    }, [target]);
    useEffect(() => { load(); }, [load]);

    const switchRow = async (payload, done) => {
        setBusy("switch");
        const result = await saveEventSwitch({ eventKey: sel.eventKey, audience: sel.audience, ...payload });
        setBusy("");
        if (!result.ok) return toast.error(result.message);
        toast.success(done);
        return onChanged();
    };
    const pickLibrary = async (libraryId) => {
        if (!libraryId) return;
        setBusy("library");
        const result = await saveEditor({ target, enabled: true, source: "library", libraryId });
        setBusy("");
        if (!result.ok) { toast.error(result.message); return; }
        toast.success("Saved. It applies within about 30 seconds.");
        onChanged(); load();
    };
    const backToBuiltIn = async () => {
        setConfirmReset(false);
        setBusy("reset");
        const result = await resetEditor(target);
        setBusy("");
        if (!result.ok) { toast.error(result.message); return; }
        toast.success("Back to the built-in wording");
        onChanged(); load();
    };
    const test = async () => {
        setBusy("test");
        const source = info?.source === "library" ? "library" : "own";
        const result = await testEditor({ target, enabled: true, source, libraryId: info?.picked?.libraryId, content: info?.content, to });
        setBusy("");
        if (!result.ok) return toast.error(result.message);
        return toast.success(result.message || "Sent");
    };

    const kind = KIND_NOTE[look.kind] || KIND_NOTE.built;
    const custom = info && ["own", "library", "missing", "legacy"].includes(info.source);
    const name = info?.source === "library" ? info.picked?.name : info?.source === "own" ? "Your wording" : info?.source === "missing" ? "Template archived or deleted" : "Built-in wording";
    const channels = CHANNELS.filter((c) => row.cells[c.key]?.applicable);

    return (
        <SidePanel open title={eventName(sel.eventKey)} subtitle={`${CATEGORY_LABEL[event.category] || event.category} · ${AUDIENCE_LABEL[sel.audience]}`} onClose={onClose} label="Message settings">
            <div className="nc-seg" role="tablist" aria-label="Channel">
                {channels.map((c) => <button key={c.key} type="button" role="tab" aria-selected={c.key === sel.channel} className={`nc-seg-btn ${c.key === sel.channel ? "is-active" : ""}`} onClick={() => onPick({ ...sel, channel: c.key })}>{c.label}</button>)}
            </div>
            <div className="nc-panel-switches">
                <label className="nc-switch">
                    <span>Send this message to {AUDIENCE_LABEL[sel.audience].toLowerCase()}s</span>
                    <input className="form-check-input" type="checkbox" role="switch" checked={row.enabled} disabled={!!busy || (event.mandatory && row.enabled)} onChange={() => switchRow({ enabled: !row.enabled }, row.enabled ? "Switched off" : "Switched on")} />
                </label>
                <label className="nc-switch">
                    <span>Send by {CHANNELS.find((c) => c.key === sel.channel)?.long}</span>
                    <input className="form-check-input" type="checkbox" role="switch" checked={!!cell.inUse} disabled={!!busy || !row.enabled} onChange={() => switchRow({ channels: cell.inUse ? row.channels.filter((c) => c !== sel.channel) : [...row.channels, sel.channel] }, cell.inUse ? "Channel switched off" : "Channel switched on")} />
                </label>
                {event.mandatory ? <div className="nc-sub">This message can't be switched off.</div> : null}
            </div>

            <div className="nc-panel-block">
                <div className="nc-panel-label">Wording</div>
                {!info ? <Skeleton height={60} /> : (
                    <>
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                            <span className="fw-bold">{name}</span>
                            <StatusChip tone={kind.tone}>{look.kind === "bad" || look.kind === "wait" ? look.label : kind.chip}</StatusChip>
                        </div>
                        <div className="nc-sub mt-1">{look.kind === "bad" && look.sub ? `${look.sub}. ` : ""}{kind.note}</div>
                        <div className="d-flex flex-wrap gap-2 mt-3">
                            <button type="button" className="btn btn-primary btn-sm" onClick={onEdit}>Edit wording</button>
                            {info.candidates?.length ? (
                                <select className="form-select form-select-sm nc-panel-pick" value="" disabled={!!busy} onChange={(e) => pickLibrary(e.target.value)} aria-label="Choose from library">
                                    <option value="">Choose from library...</option>
                                    {info.candidates.map((c) => <option key={c.libraryId} value={c.libraryId}>{c.name}</option>)}
                                </select>
                            ) : null}
                            {custom ? <button type="button" className="btn btn-link btn-sm" disabled={!!busy} onClick={() => setConfirmReset(true)}>Back to built-in</button> : null}
                        </div>
                    </>
                )}
            </div>

            <div className="nc-panel-block">
                <div className="nc-panel-label">Preview with a sample</div>
                <Preview channel={sel.channel} shown={shown} />
                {shown?.problems?.length ? <div className="nc-problem mt-2 mb-0">{shown.problems[0].message}</div> : null}
            </div>

            <div className="nc-panel-block nc-panel-test">
                {sel.channel === "push" ? null : <input className="form-control form-control-sm" placeholder={sel.channel === "email" ? "Email address" : "Phone number"} value={to} onChange={(e) => setTo(e.target.value)} aria-label="Send the test to" />}
                <button type="button" className="btn btn-outline-primary btn-sm" disabled={!!busy || !info || (sel.channel !== "push" && !to.trim())} onClick={test}>{busy === "test" ? "Sending..." : sel.channel === "push" ? "Send me a test" : "Send a test"}</button>
                <span className="nc-sub">Marked [Test]. {sel.channel === "push" ? "Goes to your own devices." : sel.channel === "whatsapp" ? "Sends what Meta approved." : "Uses the sample."}</span>
            </div>
            <ConfirmDialog open={confirmReset} title="Back to the built-in wording?" text="This message's own wording for this channel is removed. A Library template stays in the Library." confirmLabel="Use built-in" busy={busy === "reset"} onConfirm={backToBuiltIn} onCancel={() => setConfirmReset(false)} />
        </SidePanel>
    );
};

const MessagesSection = ({ carry, openTab }) => {
    const [data, setData] = useState(null);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const [audience, setAudience] = useState("");
    const [channel, setChannel] = useState("");
    const [status, setStatus] = useState(carry?.filter === "attention" ? "attention" : "all");
    const [sel, setSel] = useState(carry?.eventKey ? { eventKey: carry.eventKey, audience: carry.audience || "customer", channel: carry.channel || "push" } : null);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        const result = await fetchTemplateGrid();
        if (result.ok) setData(result.data); else { toast.error(result.message); setData({ events: [], languages: [] }); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        const want = STATUS_FILTERS.find((f) => f.key === status) || STATUS_FILTERS[0];
        const out = [];
        (data?.events || []).forEach((event) => {
            if (category && event.category !== category) return;
            if (term && !`${event.eventKey} ${eventName(event.eventKey)}`.toLowerCase().includes(term)) return;
            event.audiences.forEach((row) => {
                if (audience && row.audience !== audience) return;
                const looks = Object.fromEntries(CHANNELS.map((c) => [c.key, cellOf(row, row.cells[c.key])]));
                const checked = channel ? [looks[channel]] : Object.values(looks);
                if (channel && looks[channel].kind === "na") return;
                if (status !== "all" && !checked.some((l) => l.kind !== "na" && want.match(l.kind))) return;
                out.push({ event, row, looks });
            });
        });
        return out;
    }, [data, search, category, audience, channel, status]);
    const categories = useMemo(() => [...new Set((data?.events || []).map((e) => e.category))], [data]);
    const defaultLanguage = (data?.languages || []).find((l) => l.isDefault)?.code || "en";

    const switchGroup = async (cat, enabled) => {
        if (!window.confirm(`Switch every ${CATEGORY_LABEL[cat] || cat} message ${enabled ? "on" : "off"}?`)) return;
        const result = await saveGroupSwitch({ category: cat, audience: "all", enabled });
        if (!result.ok) { toast.error(result.message); return; }
        toast.success(`${CATEGORY_LABEL[cat] || cat} messages switched ${enabled ? "on" : "off"}`);
        load();
    };

    if (!data) return <div className="settings-card"><Skeleton height={36} className="mb-3" /><Skeleton height={320} /></div>;

    if (editing) {
        return (
            <div className="settings-card">
                <TemplateEditor
                    key={`${editing.eventKey}|${editing.audience}|${editing.channel}`}
                    target={{ kind: "event", eventKey: editing.eventKey, audience: editing.audience, channel: editing.channel, language: defaultLanguage }}
                    title={eventName(editing.eventKey)}
                    subtitle={`${CHANNELS.find((c) => c.key === editing.channel)?.label || editing.channel} for ${AUDIENCE_LABEL[editing.audience].toLowerCase()}s`}
                    languages={data.languages}
                    openTab={openTab}
                    onClose={() => { setEditing(null); load(); }}
                />
            </div>
        );
    }

    const selected = sel ? rows.find((r) => r.event.eventKey === sel.eventKey && r.row.audience === sel.audience) || (() => {
        const event = data.events.find((e) => e.eventKey === sel.eventKey);
        const row = event?.audiences.find((a) => a.audience === sel.audience);
        return event && row ? { event, row } : null;
    })() : null;

    return (
        <div className="nc-msgs">
            <div className="nc-msgs-filters">
                <input type="search" className="form-control form-control-sm nc-search" placeholder="Search messages" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search messages" />
                <select className="form-select form-select-sm" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>)}
                </select>
                <select className="form-select form-select-sm" value={audience} onChange={(e) => setAudience(e.target.value)} aria-label="Audience">
                    <option value="">Customers, staff, admins</option>
                    {Object.entries(AUDIENCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}s</option>)}
                </select>
                <select className="form-select form-select-sm" value={channel} onChange={(e) => setChannel(e.target.value)} aria-label="Channel">
                    <option value="">Every channel</option>
                    {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                {category ? (
                    <span className="nc-msgs-group">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => switchGroup(category, true)}>All {CATEGORY_LABEL[category] || category} on</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => switchGroup(category, false)}>All off</button>
                    </span>
                ) : null}
            </div>
            <div className="nc-pills" role="group" aria-label="Status">
                {STATUS_FILTERS.map((f) => <button key={f.key} type="button" className={`nc-pill ${status === f.key ? "is-active" : ""}`} aria-pressed={status === f.key} onClick={() => setStatus(f.key)}>{f.label}</button>)}
            </div>

            <div className={`nc-msgs-body ${selected ? "has-panel" : ""}`}>
                <div className="nc-msgs-table-wrap">
                    {rows.length === 0 ? <EmptyState title="No messages match" text="Change the filters or the search." /> : (
                        <table className="nc-msgs-table">
                            <thead>
                                <tr><th scope="col">Message</th><th scope="col">Who</th>{CHANNELS.map((c) => <th key={c.key} scope="col">{c.label}</th>)}</tr>
                            </thead>
                            <tbody>
                                {rows.map(({ event, row, looks }) => {
                                    const isSel = sel && sel.eventKey === event.eventKey && sel.audience === row.audience;
                                    return (
                                        <tr key={`${event.eventKey}|${row.audience}`} className={`${isSel ? "is-selected" : ""} ${row.enabled ? "" : "is-off"}`}>
                                            <th scope="row">
                                                <div className="nc-msgs-name">{eventName(event.eventKey)}</div>
                                                <div className="nc-sub">{CATEGORY_LABEL[event.category] || event.category}</div>
                                            </th>
                                            <td className="nc-msgs-who">{AUDIENCE_LABEL[row.audience]}</td>
                                            {CHANNELS.map((c) => {
                                                const l = looks[c.key];
                                                if (l.kind === "na") return <td key={c.key}><span className="nc-cell2 is-na">{l.label}</span></td>;
                                                const on = isSel && sel.channel === c.key;
                                                return (
                                                    <td key={c.key}>
                                                        <button type="button" className={`nc-cell2 is-${l.kind} ${on ? "is-open" : ""}`} aria-label={`${eventName(event.eventKey)}, ${AUDIENCE_LABEL[row.audience]}, ${c.label}: ${l.label}`} onClick={() => setSel({ eventKey: event.eventKey, audience: row.audience, channel: c.key })}>
                                                            <span>{l.label}</span>
                                                            {l.sub ? <span className="nc-cell2-sub">{l.sub}</span> : null}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                {selected ? (
                    <Panel
                        key={`${sel.eventKey}|${sel.audience}|${sel.channel}`}
                        sel={sel} row={selected.row} event={selected.event} defaultLanguage={defaultLanguage}
                        onPick={setSel} onClose={() => setSel(null)} onChanged={load} onEdit={() => setEditing(sel)}
                    />
                ) : null}
            </div>
            <p className="nc-sub mt-2">Click a channel to change it without leaving the list. Grey is the built-in wording, purple your own or a Library template, amber and red need attention. WhatsApp and SMS go to customers only; the in-app inbox always gets the push wording.</p>
        </div>
    );
};

export default MessagesSection;
